import { Button, Flex, Text } from '@mantine/core';
import { useState } from 'react';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { type DataKYCFileTct, KycFileCertificationStatus } from '@dealstack/shared/lib/generated';
import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { ChangeCertificationModal } from './ChangeCertificationModal';
import { canChangeCertification, hasCertificationToShow } from './ProofCertificationStatus.utils';
import { useKycFileCertificationActions } from './useKycFileCertificationActions';

interface Props {
	kycFile: DataKYCFileTct;
	certificationRequired: boolean;
	/** Rejected files are replaced, not re-certified. */
	isRejected?: boolean;
	onChanged: () => void;
}

/** DC-825: how an uploaded proof is certified, with resend and change actions. */
export const ProofCertificationStatus = ({
	kycFile,
	certificationRequired,
	isRejected = false,
	onChanged,
}: Props) => {
	const tInvestor = useTranslationInvestor();
	const [isChanging, setIsChanging] = useState(false);
	const { resendMutation, changeMutation } = useKycFileCertificationActions(onChanged);
	const status = kycFile.certificationStatus;

	if (!hasCertificationToShow(status, certificationRequired)) {
		return null;
	}

	const certifierName = kycFile.certification?.certifierName ?? '';
	const certifierEmail = kycFile.certification?.certifierEmail ?? '';
	const display = {
		[KycFileCertificationStatus.CERTIFIED]: {
			color: 'green',
			badge: tInvestor('investment.kyc.certification.status_certified', {
				defaultValue: 'Certified',
			}),
			detail: certifierName
				? tInvestor('investment.kyc.certification.detail_certified', {
						defaultValue: 'Certified by {{certifierName}}',
						certifierName,
					})
				: '',
		},
		[KycFileCertificationStatus.SELF_ATTESTED]: {
			color: 'green',
			badge: tInvestor('investment.kyc.certification.status_self_attested', {
				defaultValue: 'Certified copy',
			}),
			detail: tInvestor('investment.kyc.certification.detail_self_attested', {
				defaultValue: 'You confirmed this copy is already certified.',
			}),
		},
		[KycFileCertificationStatus.PENDING_CERTIFIER]: {
			color: 'yellow',
			badge: tInvestor('investment.kyc.certification.status_pending', {
				defaultValue: 'Awaiting certifier',
			}),
			detail: tInvestor('investment.kyc.certification.detail_pending', {
				defaultValue: 'Waiting for {{certifierName}} ({{certifierEmail}}) to certify it.',
				certifierName,
				certifierEmail,
			}),
		},
		[KycFileCertificationStatus.UNCERTIFIED]: {
			color: 'red',
			badge: tInvestor('investment.kyc.certification.status_uncertified', {
				defaultValue: 'Not certified',
			}),
			detail: tInvestor('investment.kyc.certification.detail_uncertified', {
				defaultValue: 'This document must be certified before it can be approved.',
			}),
		},
	}[status];

	const showActions = !isRejected && canChangeCertification(status);
	const isPending = status === KycFileCertificationStatus.PENDING_CERTIFIER;

	return (
		<Flex
			direction="column"
			gap="0.5rem"
			mt="0.5rem"
			data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_CERTIFICATION_STATUS}
			data-status={status}
		>
			<Flex align="center" gap="0.5rem" wrap="wrap">
				<StatusBadge color={display.color} textColor="dark" text={display.badge} />
				{display.detail && (
					<Text size="sm" c="dark.0">
						{display.detail}
					</Text>
				)}
			</Flex>
			{showActions && (
				<Flex gap="0.5rem">
					{isPending && (
						<Button
							size="xs"
							variant="outline"
							color="dark"
							loading={resendMutation.isPending}
							onClick={() => resendMutation.mutate(kycFile.id)}
							data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_CERTIFICATION_RESEND}
						>
							{tInvestor('investment.kyc.certification.resend', {
								defaultValue: 'Resend invitation',
							})}
						</Button>
					)}
					<Button
						size="xs"
						variant="outline"
						color="dark"
						onClick={() => setIsChanging(true)}
						data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_CERTIFICATION_CHANGE}
					>
						{status === KycFileCertificationStatus.UNCERTIFIED
							? tInvestor('investment.kyc.certification.add', {
									defaultValue: 'Certify this document',
								})
							: tInvestor('investment.kyc.certification.change', {
									defaultValue: 'Change',
								})}
					</Button>
				</Flex>
			)}
			{isChanging && (
				<ChangeCertificationModal
					kycFile={kycFile}
					isSaving={changeMutation.isPending}
					onClose={() => setIsChanging(false)}
					onSave={(choice) =>
						changeMutation.mutate(
							{ kycFileId: kycFile.id, choice },
							{ onSuccess: () => setIsChanging(false) }
						)
					}
				/>
			)}
		</Flex>
	);
};
